import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';

import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams, SortDirection } from 'ag-grid-community';
import { AgGridCustomMenuHeaderService } from './ag-grid-custom-menu-header.service';
import { Subscription } from 'rxjs';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzModalService } from 'ng-zorro-antd/modal';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HandbookInfoIconComponent } from '@wilson/handbook-info-icon';
import {
  AgGridColumnOptionModalComponent,
  WilsonAgGridColumnOptionModalInput,
} from '../ag-grid-column-option-modal/ag-grid-column-option-modal.component';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

export interface ExtraInfoPopoverConfig {
  titleKey: string;
  contentKey: string;
  linkUrl?: string;
  linkTextKey?: string;
}

@Component({
  selector: 'wilson-ag-grid-custom-menu-header',
  standalone: true,
  imports: [
    NzTooltipModule,
    FontAwesomeModule,
    TranslateModule,
    HandbookInfoIconComponent,
  ],
  templateUrl: './ag-grid-custom-menu-header.component.html',
  styleUrl: './ag-grid-custom-menu-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.center-header]': 'centerHeader',
    '[class.has-extra-info]': 'showExtraInfoIcon()',
  },
})
export class AgGridCustomMenuHeaderComponent
  implements IHeaderAngularComp, OnDestroy
{
  private agGridCustomMenuHeaderService = inject(AgGridCustomMenuHeaderService);
  private cdr = inject(ChangeDetectorRef);
  private modalService = inject(NzModalService);
  private translateService = inject(TranslateService);

  public params!: IHeaderParams;
  protected displayName!: string;
  protected headerTooltip!: string;
  protected hideColumnVisibilityMenu = false;

  // HACK: The `hideHeaderTooltip` flag exists to disable tooltips, because the
  // original implementation made tooltips opt-out instead of opt-in (always
  // showing `params.displayName` as fallback). Ideally, tooltips should only
  // show when `headerTooltip` is explicitly provided, but changing that would
  // break almost all of the existing usages that rely on the fallback behavior.
  protected hideHeaderTooltip = false;

  protected centerHeader = false;
  public sort: 'asc' | 'desc' | null = null;

  protected readonly extraInfoPopoverConfig = signal<
    ExtraInfoPopoverConfig | undefined
  >(undefined);

  protected readonly showExtraInfoIcon = computed<boolean>(
    () => !!this.extraInfoPopoverConfig(),
  );

  private subs = new Subscription();

  readonly faEllipsisVertical = faEllipsisVertical;

  agInit(params: IHeaderParams): void {
    this.refresh(params);
    this.subs = this.agGridCustomMenuHeaderService.refresh$.subscribe(
      ({ sourceColumnId }) => {
        // We don't update ourselves again
        if (this.params.column.getColId() !== sourceColumnId) {
          this.refresh(params);
          this.cdr.detectChanges();
        }
      },
    );
  }

  refresh(params: IHeaderParams): boolean {
    this.params = params;
    this.displayName = params.displayName;
    this.headerTooltip =
      params.column.getUserProvidedColDef()?.headerComponentParams
        ?.headerTooltip || '';
    this.hideColumnVisibilityMenu =
      params.column.getUserProvidedColDef()?.headerComponentParams
        ?.hideColumnVisibilityMenu ?? false;
    this.hideHeaderTooltip =
      params.column.getUserProvidedColDef()?.headerComponentParams
        ?.hideHeaderTooltip ?? false;
    this.centerHeader =
      params.column.getUserProvidedColDef()?.headerComponentParams
        ?.centerHeader ?? false;
    this.extraInfoPopoverConfig.set(
      params.column.getUserProvidedColDef()?.headerComponentParams
        ?.extraInfoPopover,
    );
    this.sort = params.column.getSort() || null;
    return true;
  }

  onHeaderClick(): void {
    if (this.params.enableSorting) {
      this.sort = this.getNextSortDirection(this.sort);
      this.updateSortDirectionForAllColumns(this.sort);
      this.params.api.onSortChanged();
    }
  }

  openColumnsModal(): void {
    const data: WilsonAgGridColumnOptionModalInput = {
      ...(this.params.column.getUserProvidedColDef()
        ?.headerComponentParams as Omit<
        WilsonAgGridColumnOptionModalInput,
        'gridApi'
      >),
      gridApi: this.params.api,
    };
    if (data) {
      this.modalService.create<
        AgGridColumnOptionModalComponent,
        WilsonAgGridColumnOptionModalInput
      >({
        nzContent: AgGridColumnOptionModalComponent,
        nzData: data,
        nzClosable: true,
        nzCentered: true,
        nzFooter: null,
        nzTitle: this.translateService.instant('general.table_column'),
      });
    }
  }

  private updateSortDirectionForAllColumns(
    newSortDirection: SortDirection,
  ): void {
    const state = {
      state: this.params.api.getColumns()?.map((column) => {
        return {
          colId: column.getColId(),
          sort:
            column.getColId() === this.params.column.getColId()
              ? newSortDirection
              : null, // Set sort for this column and reset for others
        };
      }),
      applyOrder: false,
    };

    this.params.api.applyColumnState(state);
    this.agGridCustomMenuHeaderService.trigggerUpdate(
      this.params.column.getColId(),
    );
  }

  private getNextSortDirection(
    currentSortDirection: SortDirection,
  ): SortDirection {
    if (currentSortDirection === 'asc') {
      return 'desc';
    } else if (this.sort === 'desc') {
      return null;
    } else {
      return 'asc';
    }
  }

  ngOnDestroy(): void {
    this.subs?.unsubscribe();
  }
}
